import { useTranslation } from 'react-i18next'

import {
  PopoverView,
  TertiaryButton,
  Text,
  chakra,
  useDisclosure,
} from '@enechain/ecloud-designsystem'
import { FaCircleQuestionRegular } from '@enechain/ecloud-icons'

import { Badge } from '~/components/common/Badge'
import { iconSize } from '~/constants/iconSize'

/** 承認モードの表記順は spec §1 の「要承認 → 承認なし → 自動」。 */
const MODE_ROWS = [
  { key: 'approval' },
  { key: 'noApproval' },
  { key: 'auto' },
] as const

/**
 * 入札系の業務処理で、承認モードごとに入札画面から提出した時の挙動を説明する
 * ガイド (AC-APF-035a)。
 *
 * popover はアコーディオンの区画 (`overflow: hidden`) に切られないよう portal する。
 */
export const ApprovalModeGuide = (): React.ReactElement => {
  const { t } = useTranslation()
  const disclosure = useDisclosure()

  return (
    <PopoverView
      bodyChildren={
        // DS `PopoverView` の body は内側の余白を持たないので、design
        // `.apr-legend-panel` の padding (space-3 space-4) をここで持つ。
        <chakra.div
          minWidth="360px"
          maxWidth="440px"
          lineHeight="1.6"
          paddingX={4}
          paddingY={3}
        >
          <Text
            textStyle="guidePopoverTitle"
            marginBottom={2}
            color="Text.highEmphasis"
          >
            {t('page:adminApprovalFlow.modeGuide.title')}
          </Text>
          <chakra.dl
            rowGap="6px"
            columnGap={3}
            gridTemplateColumns="max-content 1fr"
            display="grid"
            margin={0}
            color="Text.midEmphasis"
            fontSize="md"
          >
            {MODE_ROWS.map(({ key }) => (
              <chakra.div key={key} display="contents">
                <chakra.dt alignItems="center" display="flex" margin={0}>
                  <Badge
                    justifyContent="center"
                    minWidth="80px"
                    status="neutral"
                  >
                    {t(`page:adminApprovalFlow.mode.${key}`)}
                  </Badge>
                </chakra.dt>
                <chakra.dd margin={0}>
                  {t(`page:adminApprovalFlow.modeGuide.${key}`)}
                </chakra.dd>
              </chakra.div>
            ))}
          </chakra.dl>
        </chakra.div>
      }
      disclosure={disclosure}
      isPortalled
      placement="bottom-start"
      triggerChildren={
        <TertiaryButton
          aria-label={t('page:adminApprovalFlow.modeGuide.ariaLabel')}
          leftIcon={
            <FaCircleQuestionRegular
              boxSize={iconSize.guideButton}
              aria-hidden="true"
            />
          }
          size="sm"
        >
          {t('page:adminApprovalFlow.modeGuide.button')}
        </TertiaryButton>
      }
    />
  )
}
